/**
 * `AIContextEngineModel.SceneProjectUserContext.ts` — port of Unity's
 * `AIContextEngineModel.SceneProjectUserContext.cs` partial: scene hierarchy
 * digest, project digest, and user interaction context builders.
 *
 * All builders return plain text (Unity's `*Json` names are a misnomer —
 * the web port names them `*Text`-style to avoid the trap). Line shapes
 * mirror the "Hierarchy Path Format" section of the system prompt exactly:
 *
 *   0,1,2: "Part Name" (assetId:…) (takes: "Walk", "Idle") (aka de:"Zahnrad") (moved)
 *   selectionSetName:"Set Name": "Part A", "Part B"
 *   textBoxName:"Label": "Preview text"
 *
 * The `(aka …)` segment carries part name aliases — a web-first field with no
 * Unity counterpart. It is documented in the web copy of `AI/prompt/base.md`;
 * keep the two in step.
 *
 * The `(takes: …)` segment is the THIRD web-first divergence — it lists a
 * model's animation clips so the agent can name one to `Toggle_Model_Animation`.
 * Its project-digest counterpart `(hasAnimation:…)` is Unity parity.
 *
 * The project digest's `Documents:` line is the SECOND web-first divergence
 * here, and a deliberate one: parity with the desktop digest is not tracked for
 * it (direction call, 2026-08-26). It is additive and emits nothing for a
 * project with no documents, so it cannot change the shape Unity produces for
 * that case.
 */

import type { IProjectModel } from "@models/project/IProjectModel";
import type { EntityData } from "@models/data/entityData";
import { docFileNameFromStoragePath } from "@models/data/docsManifestDto";
import { buildSceneMergedAncestorIndex } from "@models/data/entityData";
import { Ioc } from "@core/ioc/ioc";
import { ORBIT_CAMERA, type OrbitCameraController } from "@runtime/camera/orbitCameraController";
import { MathUtils } from "three";
import {
  PersistentHighlightThicknessModes,
  highlightContextColorLabel,
  highlightContextPulseLabel,
  highlightContextThicknessLabel,
  parsePersistentHighlightJson,
} from "@models/data/persistentHighlightData";
import { aliasSegmentOf, buildPathIndex, displayNameOf, isMoved } from "./hierarchyPaths";
import { getAssetAnimationTakes } from "@runtime/assets/animationClips";

/** Unity `AIContextEngineModel.MaxNodes` hierarchy walk cap. */
const MAX_NODES = 20000;
/** Unity text-box preview cap. */
const TEXT_PREVIEW_MAX = 120;

export const EMPTY_SCENE_TEXT =
  "Scene is empty. Import models or copy them from other scene";

export function buildSceneContextText(project: IProjectModel): string {
  const sceneId = project.activeScene?.id;
  if (!sceneId) return EMPTY_SCENE_TEXT;

  const entities = project.getEntities(sceneId);
  const tiles = project.getMediaTiles(sceneId);
  const sets = project.getSelectionSets(sceneId);
  if (entities.length === 0 && tiles.length === 0) return EMPTY_SCENE_TEXT;

  const lines: string[] = [];

  if (sets.length > 0) {
    lines.push("Selection Sets:");
    const nameForSnapshot = new Map<string, string>();
    for (const entry of buildPathIndex(entities)) {
      nameForSnapshot.set(entry.snapshot.id, displayNameOf(entry.snapshot));
    }
    for (const set of sets) {
      const memberNames = (set.EntitySnapshotIDs ?? [])
        .map((id) => nameForSnapshot.get(id))
        .filter((n): n is string => !!n)
        .map((n) => `"${n}"`);
      lines.push(`selectionSetName:"${set.Name}": ${memberNames.join(", ")}`);
    }
    lines.push("");
  }

  if (tiles.length > 0) {
    lines.push("Text Boxes:");
    for (const tile of tiles) {
      const text = (tile.asset?.text ?? "").replace(/\s+/g, " ").trim();
      const preview =
        text.length === 0
          ? "(empty)"
          : `"${text.length > TEXT_PREVIEW_MAX ? `${text.slice(0, TEXT_PREVIEW_MAX)}...` : text}"`;
      // Unity `BuildTextBoxContextEntry` + `BuildHighlightStateSuffix`.
      const highlight = highlightIndicatorOf(tile.persistentHighlightJson);
      lines.push(`textBoxName:"${tile.name}": ${preview}${highlight ? ` (${highlight})` : ""}`);
    }
    lines.push("");
  }

  // Unity's `BuildNodeFlat` returns before recursing once a node
  // `HasMergedChildren`, so a merged node is listed but the parts folded into
  // it are not. The agent must not be told about parts the user cannot see or
  // click. Path numbering is unaffected — it comes from persisted hierarchy
  // sibling addresses, and `resolveByPath` still indexes the full snapshot
  // set that the Web renderer retains for merged descendants.
  const mergedAncestors = buildSceneMergedAncestorIndex(entities);
  let truncated = false;
  let count = 0;
  for (const entry of buildPathIndex(entities)) {
    if (mergedAncestors.has(entry.snapshot.id)) continue;
    if (count >= MAX_NODES) {
      truncated = true;
      break;
    }
    count++;
    lines.push(formatPartLine(entry.path, entry));
  }

  const body = lines.join("\n");
  return truncated ? `(truncated)\n${body}` : body;
}

function formatPartLine(
  path: string,
  entry: { snapshot: import("@models/data/entityData").EntitySnapshot; entity: EntityData; isRoot: boolean },
): string {
  let line = `${path}: "${displayNameOf(entry.snapshot)}"`;
  if (entry.isRoot && entry.entity.assetId) {
    line += ` (assetId:${entry.entity.assetId})`;
    // Animation TAKES, on the root line only — clips belong to the asset, not
    // to a part. Web-first, and it lives on the SCENE digest rather than the
    // project one because this digest is built only when `Get_Scene_Context`
    // is called and therefore costs nothing per turn, where the project digest
    // rides every turn (see `buildProjectContextText`, which carries only the
    // boolean Unity prints).
    //
    // ⚠ UNKNOWN emits NOTHING. `getAssetAnimationTakes` answers `null` for an
    // asset that is not decoded yet, and a literal `(takes: unknown)` would be
    // agent-facing text asserting a state the agent then reasons from ("they
    // are still loading, I should wait") when the truth may be "this client can
    // never decode this format". A DECODED model with zero clips is a real
    // fact and does print — that is what separates it from unknown.
    //
    // ⚠ It prints `(takes: none)` and NOT "(no animation)". `querySceneContext`
    // filters this digest by SUBSTRING, so a line containing the word a user
    // searches for is returned — and "no animation" would make a search for
    // "animation" answer with exactly the models that have none.
    const takes = getAssetAnimationTakes(entry.entity.assetId);
    if (takes !== null) {
      line += takes.length > 0
        ? ` (takes: ${takes.map((take) => `"${take}"`).join(", ")})`
        : " (takes: none)";
    }
  }
  // Part name aliases, after the identity segments and before the state block.
  // `querySceneContext` filters these lines by substring, so printing them here
  // is also what makes a part findable by an alias — no separate index.
  line += aliasSegmentOf(entry.snapshot);
  const states: string[] = [];
  if (isMoved(entry.snapshot)) states.push("moved");
  const highlight = highlightIndicatorOf(entry.snapshot.persistentHighlightJson);
  if (highlight) states.push(highlight);
  if (states.length > 0) line += ` (${states.join(", ")})`;
  return line;
}

/**
 * `highlighted:red, pulsing, thin` — the color always prints, the pulse only
 * when there is one, and the thickness only when it is NOT the default thick,
 * so a plain default highlight stays short. Unity `BuildHighlightStateSuffix`,
 * shared by a part's line and a text box's. `null` when not highlighted.
 */
function highlightIndicatorOf(json: string | null): string | null {
  const highlight = parsePersistentHighlightJson(json);
  if (!highlight) return null;
  let indicator = `highlighted:${highlightContextColorLabel(highlight)}`;
  const pulse = highlightContextPulseLabel(highlight);
  if (pulse) indicator += `, ${pulse}`;
  const thickness = highlightContextThicknessLabel(highlight);
  if (thickness && thickness !== PersistentHighlightThicknessModes.Thick) {
    indicator += `, ${thickness}`;
  }
  return indicator;
}

/**
 * Cap on the document names listed in the project digest. A project row holds a
 * handful in practice; this only bounds the per-turn cost of a pathological one.
 */
const K_MAX_CONTEXT_DOCUMENTS = 25;

export function buildProjectContextText(project: IProjectModel): string {
  const active = project.activeProject;
  if (!active) return `{"project":null}`;

  const lines: string[] = [`Project: "${active.name}"`];

  // Web-first: Unity's digest carries no document line (see the file header).
  //
  // Without it the standing prompt gives the model NO evidence that the project
  // has documents at all, so a question the Answer skill routes to
  // `Prompt_Search_Docs` reads as "there is nothing here to search" and lands on
  // the catch-all refusal instead. Observed: "how fast can robodog run" refused
  // three times on a project carrying two Robodog manuals that answer it.
  //
  // AN EMPTY LIST MEANS UNKNOWN, NOT NONE, so nothing is emitted for it - never
  // a "No documents." line. `ProjectDocsModel.effectiveProjectDocPaths` prefers
  // this row and falls back to the MANIFEST when it is empty, precisely because
  // "a freshly-opened project whose row was written by another client must not
  // read as empty"; the row is also only refined once that model has loaded.
  // Announcing absence from it would talk the agent out of the one search that
  // could settle the question. Silence leaves today's behaviour byte-identical
  // for a project with no documents, so this change is purely additive.
  const documents = (active.documentStoragePaths ?? [])
    .map((path) => docFileNameFromStoragePath(path))
    .filter((name) => name.length > 0);
  if (documents.length > 0) {
    // Capped because this rides EVERY turn. Truncation is safe here only because
    // the list is evidence, never an answer: the agent must still search, and the
    // overflow marker stops a short list reading as the whole library.
    const shown = documents.slice(0, K_MAX_CONTEXT_DOCUMENTS);
    let line = `Documents: ${shown.map((n) => `"${n}"`).join(", ")}`;
    const hidden = documents.length - shown.length;
    if (hidden > 0) line += `, and ${hidden} more`;
    lines.push(line);
  }

  const scenes = project.scenes;
  const activeSceneId = project.activeSceneId;
  if (scenes.length === 0) {
    lines.push("No scenes.");
    return lines.join("\n");
  }

  lines.push("Available Scenes:");
  scenes.forEach((scene, i) => {
    const number = i + 1;
    const isActive = scene.id === activeSceneId;
    let line = `  Scene ${number}: "${scene.name}"${isActive ? " (currently active)" : ""}`;
    const notes = scene.presenterNotes?.trim();
    if (notes) line += `\n    Notes: ${notes.replace(/\s+/g, " ")}`;
    // `(hasAnimation:true)` matches Unity's own per-model indicator. This digest
    // DOES ride every turn, so it carries the boolean and not the take names —
    // the names are on the scene digest, which is only built when asked for.
    // Omitted entirely when unknown, like the `Documents:` line below.
    const models = project
      .getEntities(scene.id)
      .flatMap((e) => e.snapshots
        .filter((s) => s.isRoot)
        .map((s) => {
          const takes = getAssetAnimationTakes(e.assetId);
          const animation = takes === null ? "" : ` (hasAnimation:${takes.length > 0})`;
          return `"${displayNameOf(s)}"${animation}`;
        }));
    if (models.length > 0) line += `\n    Models: ${models.join(", ")}`;
    lines.push(line);
  });
  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// User interaction context (selection / pointing with a 5 s TTL)
// ---------------------------------------------------------------------------

/** Unity `LastContextLifetimeSeconds`. */
const LAST_CONTEXT_LIFETIME_MS = 5000;

export interface UserInteractionState {
  readonly selectedSnapshotIds: ReadonlyArray<string>;
  readonly lastSelectionAt: number;
  readonly lastSelectedSnapshotIds: ReadonlyArray<string>;
  /** What the user is pointing at right now — the web's cursor hover. */
  readonly pointedSnapshotId: string | null;
  readonly lastPointedAt: number;
  readonly lastPointedSnapshotId: string | null;
}

/** Question-time interaction state enriched with names from the active scene. */
export interface UserInteractionSnapshot extends UserInteractionState {
  readonly snapshotNames: Readonly<Record<string, string>>;
}

function isFresh(at: number): boolean {
  return Date.now() - at <= LAST_CONTEXT_LIFETIME_MS;
}

export function hasUserInteractionContext(state: UserInteractionState): boolean {
  if (state.pointedSnapshotId) return true;
  if (state.selectedSnapshotIds.length > 0) return true;
  if (state.lastPointedSnapshotId && isFresh(state.lastPointedAt)) return true;
  return (
    state.lastSelectedSnapshotIds.length > 0 && isFresh(state.lastSelectionAt)
  );
}

export function buildUserContextText(
  project: IProjectModel,
  state: UserInteractionState,
  isWriter: boolean,
): string {
  const lines: string[] = [`User role: ${isWriter ? "Writer" : "Viewer"}`];

  if (Ioc.isRegistered<OrbitCameraController>(ORBIT_CAMERA)) {
    const camera = Ioc.resolve<OrbitCameraController>(ORBIT_CAMERA).camera;
    const p = camera.position;
    const e = camera.rotation;
    lines.push(
      `User camera: position=(${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)}) ` +
        `rotation=(${MathUtils.radToDeg(e.x).toFixed(1)}, ${MathUtils.radToDeg(e.y).toFixed(1)}, ${MathUtils.radToDeg(e.z).toFixed(1)})`,
    );
  }

  const sceneId = project.activeScene?.id;
  const entities = sceneId ? project.getEntities(sceneId) : [];
  const index = buildPathIndex(entities);

  // Aliases ride along so "what is this?" can answer in the user's own word
  // for the part ("the Fan-part 001, also known as Blade") without a second
  // lookup — the same `(aka …)` grammar the scene digest prints.
  const describe = (snapshotId: string): string | null => {
    const entry = index.find((e) => e.snapshot.id === snapshotId);
    if (!entry) return null;
    return `${entry.path}: "${displayNameOf(entry.snapshot)}"${aliasSegmentOf(entry.snapshot)}`;
  };

  // Exactly one referent block, in the precedence the system prompt states:
  // pointing beats selection, and the ~5 s memories only speak when neither
  // is current.
  const pointed = state.pointedSnapshotId ? describe(state.pointedSnapshotId) : null;
  if (pointed) {
    lines.push(`User pointing at: ${pointed}`);
    return lines.join("\n");
  }

  if (state.selectedSnapshotIds.length > 0) {
    const described = state.selectedSnapshotIds
      .map(describe)
      .filter((d): d is string => !!d);
    if (described.length === 1) {
      lines.push(`Currently Selected: ${described[0]}`);
    } else if (described.length > 1) {
      lines.push(`Currently Selected (${described.length} parts):`);
      described.forEach((d, i) => lines.push(`  Part ${i + 1}: ${d}`));
    }
  } else if (state.lastPointedSnapshotId && isFresh(state.lastPointedAt)) {
    // The common case for a typed question: the user hovers a part, then moves
    // the cursor to the panel to write, which clears the hover before the turn
    // is sent. Without this memory "what is this?" would arrive with nothing
    // attached.
    const described = describe(state.lastPointedSnapshotId);
    if (described) lines.push(`Last Pointed Part: ${described}`);
  } else if (
    state.lastSelectedSnapshotIds.length > 0 &&
    isFresh(state.lastSelectionAt)
  ) {
    const described = state.lastSelectedSnapshotIds
      .map(describe)
      .filter((d): d is string => !!d);
    if (described.length > 0) {
      lines.push(`Last Selected Part: ${described[0]}`);
    }
  }

  return lines.join("\n");
}
